/**
 * @fileoverview Page for recipients to view their documents
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { documentService } from '@/services/documents/documentService';
import { documentAccessService } from '@/services/documents/documentAccessService';
import { formatDate } from '@/utils/formatters';

export const MyDocumentsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Default to list view on desktop (lg breakpoint), grid on mobile
  const [viewMode, setViewMode] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'list' : 'grid'
  );

  useEffect(() => {
    if (!user || !user.email) {
      navigate('/login');
      return;
    }
    loadDocuments();
  }, [user]);

  const loadDocuments = () => {
    if (!user?.email) return;

    setIsLoading(true);
    try {
      // Get documents where user is recipient
      const recipientDocs = documentService.getDocumentsByRecipient(user.email);

      // Get documents where user has been granted access
      const userAccesses = documentAccessService.getUserAccesses(user.email);
      const accessDocIds = userAccesses.map(a => a.documentId);
      const accessDocs = accessDocIds
        .map(id => documentService.getDocumentById(id))
        .filter(d => d !== null);

      // Combine and remove duplicates
      const allDocs = [...recipientDocs, ...accessDocs];
      const uniqueDocs = allDocs.filter((doc, index, self) =>
        index === self.findIndex(d => d.id === doc.id)
      );

      setDocuments(uniqueDocs);
      setAccesses(userAccesses);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = (documentId) => {
    navigate(`/document/${documentId}`);
  };

  const getAccessInfo = (documentId) => {
    return accesses.find(a => a.documentId === documentId);
  };

  if (isLoading) {
    return (
      <div className="flex-1 w-full p-4 md:p-6 lg:p-10">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('common.loading', 'Cargando documentos...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full p-4 md:p-6 lg:p-10">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>{t('mydocs.title', 'Mis Certificados')}</h1>
          <p>{t('mydocs.subtitle', 'Gestiona y visualiza tus documentos certificados')}</p>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title={t('mydocs.grid_view', 'Vista Cuadrícula')}
          >
            <i className="fas fa-th-large"></i>
          </button>
          <button
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title={t('mydocs.list_view', 'Vista Lista')}
          >
            <i className="fas fa-list"></i>
          </button>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>{t('mydocs.no_certs', 'No tienes certificados')}</h3>
          <p>{t('mydocs.no_docs_found', 'No se encontraron documentos asociados a tu email.')}</p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {documents.map((doc) => {
                const access = getAccessInfo(doc.id);
                return (
                  <div key={doc.id} className="document-card h-full">
                    <div className="document-card-header">
                      <div className="document-icon">
                        <i className="fas fa-certificate"></i>
                      </div>
                      <div className="document-status">
                        <span className={`status-badge status-${doc.status}`}>
                          {doc.status === 'issued' ? t('history.status_issued', 'Emitido') :
                            doc.status === 'verified' ? t('history.status_verified', 'Verificado') :
                              doc.status === 'revoked' ? t('history.status_revoked', 'Revocado') : t('history.status_pending', 'Pendiente')}
                        </span>
                      </div>
                    </div>

                    <div className="document-card-body">
                      <h3>{doc.templateName || t('mydocs.document', 'Documento')}</h3>
                      <p className="document-issuer">
                        <i className="fas fa-user-tie"></i>
                        {t('mydocs.issued_by', 'Emitido por')}: {doc.issuerName}
                      </p>
                      <p className="document-date">
                        <i className="fas fa-calendar"></i>
                        {formatDate(doc.issuedAt.toString())}
                      </p>

                      {access && (
                        <div className="access-info">
                          <i className="fas fa-share-alt"></i>
                          <span>{t('mydocs.shared_access', 'Acceso compartido')}</span>
                          {access.expiresAt && (
                            <span className="expires-at">
                              ({t('mydocs.expires', 'Vence')}: {formatDate(access.expiresAt.toString())})
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="document-card-footer">
                      <button
                        className="btn-view-document"
                        onClick={() => handleViewDocument(doc.id)}
                      >
                        <i className="fas fa-eye"></i>
                        {t('mydocs.view_doc', 'Ver Documento')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="documents-list-container">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>{t('mydocs.document', 'Documento')}</th>
                    <th>{t('mydocs.issuer', 'Emisor')}</th>
                    <th>{t('mydocs.date', 'Fecha')}</th>
                    <th>{t('history.status', 'Estado')}</th>
                    <th>{t('tenant.actions', 'Acciones')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div className="table-doc-info">
                          <i className="fas fa-file-alt"></i>
                          <span>{doc.templateName || t('mydocs.document', 'Documento')}</span>
                        </div>
                      </td>
                      <td>{doc.issuerName}</td>
                      <td>{formatDate(doc.issuedAt.toString())}</td>
                      <td>
                        <span className={`status-badge status-${doc.status} sm`}>
                          {doc.status === 'issued' ? t('history.status_issued', 'Emitido') :
                            doc.status === 'verified' ? t('history.status_verified', 'Verificado') : t('common.other', 'Otro')}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-icon-action"
                          onClick={() => handleViewDocument(doc.id)}
                          title={t('mydocs.view_doc', 'Ver Documento')}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

